import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useL10n } from '@uplift/hooks';
import { Button } from '../../../Button';
import { Modal, ModalActions, ModalContent } from '../../../Modal';
import { useCallback, useMemo } from 'react';
import { InsuranceSection } from '../Settings/types';
import { useDeleteInsurance, useInsurances } from '../hooks/use-insurance.hook';
import { useCurrentuser } from '@uplift/contexts';
import { useUserMatchesQuery } from '@uplift/queries';
import { ProviderMedicalRole } from '@uplift/types';

export type DeleteInsuranceModalProps = {
  onClose: () => void;
  deleteInsuranceSection: InsuranceSection;
  onAddNewInsurance: () => void;
  userId: string;
};

export const DeleteInsuranceModal: React.VFC<DeleteInsuranceModalProps> = ({ onClose, deleteInsuranceSection, onAddNewInsurance, userId }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const { classes: modalClasses } = useModalStyles();
  const { data: insurancesData } = useInsurances(userId);
  const { loading, onDeleteInsurance, onDeletePrimaryAndReplaceWithSecondary } = useDeleteInsurance(deleteInsuranceSection, userId, onClose);
  const me = useCurrentuser();
  const { data: matches } = useUserMatchesQuery(userId);
  const activeMatches = matches?.activeMatches || [];
  const psychProviderOnly = activeMatches.some(
    m => m.provider.medicalRole === ProviderMedicalRole.psychiatrist
  ) && !activeMatches.some(m => m.provider.medicalRole === ProviderMedicalRole.therapist);


  const secondaryInsuranceData = insurancesData?.[InsuranceSection.secondaryInsurance];
  const deletePrimaryAndReplace = deleteInsuranceSection === InsuranceSection.primaryInsurance && !!secondaryInsuranceData;
  const deleteSecondary = deleteInsuranceSection === InsuranceSection.secondaryInsurance;
  const deletePrimary = deleteInsuranceSection === InsuranceSection.primaryInsurance && !secondaryInsuranceData;
  const cashPayAmount = psychProviderOnly ? '$250' : '$125–$145';

  const onAddNewInsuranceHandler = useCallback(() => {
    onClose();
    onAddNewInsurance();
  }, [ onAddNewInsurance, onClose ]);

  const content = useMemo(() => {
    let content: React.ReactNode = '';

    if (deletePrimaryAndReplace) {
      content = (
        <Box>
          <Typography>{l10n.accountSettings.insurance.removingMightChangeCost}&nbsp;{l10n.accountSettings.insurance.emailForUpdatedCost}</Typography>
          <Typography>{l10n.accountSettings.insurance.useForPrimaryInsurance}</Typography>
        </Box>
      );
    } else if (deleteSecondary) {
      content = <Typography>{l10n.accountSettings.insurance.removingMightChangeCost}</Typography>;
    } else if (deletePrimary) {
      content = <Typography>{l10n.accountSettings.insurance.ifDeleteInsurance(cashPayAmount)}</Typography>;
    }


    return (
      <ModalContent>
        {content}
      </ModalContent>
    );
  }, [ l10n.accountSettings.insurance, me ]);

  const title = deletePrimaryAndReplace
    ? l10n.accountSettings.insurance.deleteAndReplaceInsurance
    : l10n.accountSettings.insurance.deleteInsurance;

  const actions = useMemo(() => {
    const cancelButton = (
      <Button onClick={onClose} disabled={loading}>{l10n.general.cancel}</Button>
    );

    if (deletePrimaryAndReplace && secondaryInsuranceData) {
      return (
        <ModalActions className={classes.actionsBlock}>
          <Button
            variant="contained"
            color="secondary"
            onClick={onDeletePrimaryAndReplaceWithSecondary}
          >
            {l10n.accountSettings.insurance.useInsurance(secondaryInsuranceData.companyName)}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={onAddNewInsuranceHandler}
          >
            {l10n.accountSettings.insurance.addNewInsurance}
          </Button>
          {cancelButton}
        </ModalActions>
      );
    } else {
      return (
        <ModalActions className={classes.actionsInline}>
          {cancelButton}
          <Button
            onClick={onDeleteInsurance}
            loading={loading}
            disabled={loading}
            variant="contained"
            color="error"
          >
            {l10n.accountSettings.insurance.deleteInsurance}
          </Button>
        </ModalActions>
      );
    }
  }, [ classes.actionsBlock, classes.actionsInline, deletePrimaryAndReplace, l10n.accountSettings.insurance, l10n.general.cancel, loading, onAddNewInsuranceHandler, onClose, onDeleteInsurance, onDeletePrimaryAndReplaceWithSecondary, secondaryInsuranceData ]);

  return (
    <Modal
      title={title}
      fullWidth
      open
      showCloseIcon={false}
      classes={modalClasses}
      onClose={onClose}
      maxWidth={deletePrimaryAndReplace ? 'sm' : 'md'}
    >
      {content}
      {actions}
    </Modal>
  );
};

const useModalStyles = makeStyles()({
  paperWidthMd: {
    width: '600px',
    maxWidth: '600px'
  },
});

const useStyles = makeStyles()((theme) => ({
  actionsInline: {
    '& > :not(:first-child)': {
      margin: 0
    },
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      '& > button': {
        width: '100%'
      }
    }
  },
  actionsBlock: {
    '& > :not(:first-child)': {
      margin: 0
    },
    display: 'flex',
    gap: theme.spacing(1),
    flexDirection: 'column',
    '& > button': {
      width: '100%',
    }
  },
}));
